import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Divider,
  Chip,
  Alert
} from '@mui/material';
import {
  Search,
  Person,
  Schedule,
  AccessTime
} from '@mui/icons-material';
import { API_URL } from '../utils/apiConfig';
import { AuthContext } from '../context/AuthContext';

export default function EmployeePickerDialog({
  open,
  onClose,
  onEmployeeSelect,
  selectedDate,
  startTime = '09:00',
  endTime = '17:00',
  locationId
}) {
  const { user } = useContext(AuthContext) || {};
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');

  // Format date for display
  const formatDate = (date) => {
    return date?.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });
  };

  // Fetch employees when dialog opens
  useEffect(() => {
    if (open && (!employees.length || employees.locationId !== locationId)) {
      fetchEmployees();
    }
  }, [open, locationId]);

  // Filter employees based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredEmployees(employees);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = employees.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(query) ||
        emp.role?.toLowerCase().includes(query)
      );
      setFilteredEmployees(filtered);
    }
  }, [searchQuery, employees]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (locationId || user?.store) params.set('locationId', locationId || user.store);
      params.set('limit', '100');

      const response = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/employees?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (response.ok) {
        const employeeList = data.data || [];
        employeeList.locationId = locationId; // Track which location this is for
        setEmployees(employeeList);
        setFilteredEmployees(employeeList);
      } else {
        setError(data.error || 'Failed to load employees');
      }
    } catch (err) {
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeClick = (employee) => {
    if (onEmployeeSelect) {
      onEmployeeSelect({
        employee,
        date: selectedDate,
        startTime,
        endTime
      });
    }
    handleClose();
  };

  const handleClose = () => {
    setSearchQuery('');
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{
        pb: 1,
        borderBottom: '1px solid #e5e7eb'
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
            Assign Shift
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
            {formatDate(selectedDate)} • {startTime} - {endTime}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Search Bar */}
        <Box sx={{ p: 3, pb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search employees..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#6b7280' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#f9fafb'
              }
            }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 3, mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Employee List */}
        <List sx={{ px: 1, maxHeight: 400, overflow: 'auto' }}>
          {loading ? (
            <ListItem>
              <ListItemText
                primary="Loading employees..."
                sx={{ textAlign: 'center', color: '#6b7280' }}
              />
            </ListItem>
          ) : filteredEmployees.length === 0 ? (
            <ListItem>
              <ListItemText
                primary={searchQuery ? "No employees found" : "No employees available"}
                sx={{ textAlign: 'center', color: '#6b7280' }}
              />
            </ListItem>
          ) : (
            filteredEmployees.map((employee) => (
              <ListItem key={employee.id} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleEmployeeClick(employee)}
                  sx={{
                    borderRadius: 2,
                    mx: 2,
                    '&:hover': {
                      backgroundColor: '#f0f9ff'
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#3b82f6',
                      fontWeight: 600
                    }}>
                      {employee.first_name?.charAt(0)}{employee.last_name?.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{
                        fontWeight: 600,
                        color: '#111827'
                      }}>
                        {employee.first_name} {employee.last_name}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={employee.role}
                          size="small"
                          sx={{
                            backgroundColor: '#e5e7eb',
                            color: '#374151',
                            fontSize: '0.75rem',
                            height: 20
                          }}
                        />
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))
          )}
        </List>
      </DialogContent>

      <DialogActions sx={{
        p: 3,
        borderTop: '1px solid #e5e7eb',
        justifyContent: 'space-between'
      }}>
        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          <AccessTime sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
          {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''} available
        </Typography>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}